import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import type {
  Calendar,
  Event,
  User,
  ConflictWarning,
  CalendarSubscription,
  CalendarSubscriber,
} from 'commoncal-shared';

async function getAuthHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Timezone':
      Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function captureApiError(
  err: unknown,
  path: string,
  method: string | undefined,
  status: number | string
): void {
  Sentry.captureException(err, {
    tags: {
      'api.route': path,
      'api.method': method ?? 'GET',
      'api.status': String(status),
    },
  });
}

async function apiFetch<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const headers = await getAuthHeaders();
  const method = init?.method;

  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: { ...headers, ...(init?.headers ?? {}) },
    });
  } catch (err) {
    captureApiError(err, path, method, 'network');
    throw err;
  }

  let json: {
    success?: boolean;
    error?: string;
    data?: unknown;
  };
  try {
    json = await res.json();
  } catch (err) {
    captureApiError(err, path, method, res.status);
    throw new ApiError('invalid_response', res.status);
  }

  if (!json.success) {
    const code = json.error ?? 'request_failed';
    const err = new ApiError(code, res.status);
    if (res.status >= 500) {
      captureApiError(err, path, method, res.status);
    }
    throw err;
  }

  return json.data as T;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

export const api = {
  me: {
    get: () =>
      apiFetch<{ user: User }>(
        `/me?tz=${encodeURIComponent(
          Intl.DateTimeFormat().resolvedOptions().timeZone
        )}`
      ),
    update: (body: {
      display_name?: string;
      holiday_region?: string | null;
    }) =>
      apiFetch<{ user: User }>('/me', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  calendars: {
    list: () =>
      apiFetch<{
        calendars: (Calendar & { role: string })[];
      }>('/calendars'),
    get: (id: string, params?: Record<string, string>) =>
      apiFetch<{ calendar: Calendar & { role: string } }>(
        `/calendars/${id}${params ? '?' + new URLSearchParams(params).toString() : ''}`
      ),
    create: (body: {
      name: string;
      color?: string;
      description?: string;
      timezone?: string;
      warn_on_conflicts?: boolean;
      is_kocal?: boolean;
    }) =>
      apiFetch<{ calendar: Calendar }>('/calendars', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (
      id: string,
      body: Partial<{
        name: string;
        color: string;
        description: string | null;
        timezone: string;
        warn_on_conflicts: boolean;
        is_kocal: boolean;
      }>
    ) =>
      apiFetch<{ calendar: Calendar }>(`/calendars/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      apiFetch<{ deleted: boolean }>(`/calendars/${id}`, {
        method: 'DELETE',
      }),
    leaveMembership: (id: string) =>
      apiFetch<{ left: boolean }>(
        `/calendars/${id}/membership`,
        { method: 'DELETE' }
      ),
    members: (id: string) =>
      apiFetch<{
        members: {
          user_id: string;
          role: string;
          display_name: string;
          email: string;
        }[];
      }>(`/calendars/${id}/members`),
    joinKocal: (calendarId: string, joinToken: string) =>
      apiFetch<{
        calendar_id: string;
        calendar_name: string;
      }>(`/calendars/${calendarId}/join-kocal`, {
        method: 'POST',
        body: JSON.stringify({ join_token: joinToken }),
      }),
    contribute: (
      calendarId: string,
      body: {
        title: string;
        description?: string;
        address?: string;
        phone?: string;
        start_at: string;
        end_at: string;
        all_day?: boolean;
        rrule?: string;
        exdate?: string[];
        tags?: string[];
      }
    ) =>
      apiFetch<{
        event: Event;
        mirror: {
          mirror_event_id: string;
          target_calendar_id: string;
        };
        conflicts: ConflictWarning;
      }>(`/calendars/${calendarId}/contribute`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    listMutedSources: (calendarId: string) =>
      apiFetch<{
        muted_sources: {
          source_calendar_id: string;
          source_name: string;
          muted_by: string;
          muted_at: string;
        }[];
      }>(`/calendars/${calendarId}/muted-sources`),
    muteSource: (
      calendarId: string,
      sourceCalendarId: string
    ) =>
      apiFetch<{ muted: boolean }>(
        `/calendars/${calendarId}/muted-sources`,
        {
          method: 'POST',
          body: JSON.stringify({
            source_calendar_id: sourceCalendarId,
          }),
        }
      ),
    unmuteSource: (
      calendarId: string,
      sourceCalendarId: string
    ) =>
      apiFetch<{ unmuted: boolean }>(
        `/calendars/${calendarId}/muted-sources`,
        {
          method: 'DELETE',
          body: JSON.stringify({
            source_calendar_id: sourceCalendarId,
          }),
        }
      ),
  },

  events: {
    list: (
      calendarId: string,
      params?: {
        from?: string;
        to?: string;
        join_token?: string;
      }
    ) => {
      const qs = new URLSearchParams();
      if (params?.from) qs.set('from', params.from);
      if (params?.to) qs.set('to', params.to);
      if (params?.join_token)
        qs.set('join_token', params.join_token);
      const query = qs.toString() ? `?${qs}` : '';
      return apiFetch<{ events: Event[] }>(
        `/calendars/${calendarId}/events${query}`
      );
    },
    get: (id: string) =>
      apiFetch<{ event: Event }>(`/events/${id}`),
    create: (body: {
      calendar_id: string;
      title: string;
      description?: string;
      address?: string;
      phone?: string;
      start_at: string;
      end_at: string;
      start_date?: string;
      end_date?: string;
      all_day?: boolean;
      rrule?: string;
      exdate?: string[];
      tags?: string[];
      as_kocal?: boolean;
    }) =>
      apiFetch<{
        event: Event;
        conflicts: ConflictWarning;
      }>('/events', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (
      id: string,
      body: Partial<{
        title: string;
        description: string | null;
        address: string | null;
        phone: string | null;
        start_at: string;
        end_at: string;
        start_date: string;
        end_date: string;
        all_day: boolean;
        rrule: string | null;
        exdate: string[];
        tags: string[];
      }>
    ) =>
      apiFetch<{
        event: Event;
        conflicts: ConflictWarning;
      }>(`/events/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (
      id: string,
      scope?: 'all' | 'future' | 'this',
      occurrenceDate?: string
    ) => {
      const params = new URLSearchParams();
      if (scope) params.set('scope', scope);
      if (occurrenceDate)
        params.set('date', occurrenceDate);
      const qs = params.toString();
      return apiFetch<{ deleted: boolean }>(
        `/events/${id}${qs ? `?${qs}` : ''}`,
        {
          method: 'DELETE',
        }
      );
    },
    exceptionExclude: (id: string) =>
      apiFetch<{ excluded: boolean }>(
        `/events/${id}/exception-exclude`,
        {
          method: 'POST',
        }
      ),
    exceptionRestore: (id: string) =>
      apiFetch<{ restored: boolean }>(
        `/events/${id}/exception-restore`,
        {
          method: 'POST',
        }
      ),
    mirror: (eventId: string, targetCalendarId: string) =>
      apiFetch<{
        mirror: {
          mirror_event_id: string;
          target_calendar_id: string;
        };
      }>(`/events/${eventId}/mirror`, {
        method: 'POST',
        body: JSON.stringify({
          target_calendar_id: targetCalendarId,
        }),
      }),
    unmirror: (eventId: string, targetCalendarId: string) =>
      apiFetch<{ deleted: boolean }>(
        `/events/${eventId}/mirror/${targetCalendarId}`,
        {
          method: 'DELETE',
        }
      ),
    listMirrors: (eventId: string) =>
      apiFetch<{
        mirrors: {
          mirror_event_id: string;
          target_calendar_id: string;
          target_calendar_name: string;
        }[];
      }>(`/events/${eventId}/mirrors`),
  },

  billing: {
    status: () =>
      apiFetch<{
        plan: {
          status: string;
          tier: string;
          calendar_limit: number;
          trial_ends_at: string | null;
          is_paid: boolean;
        };
      }>('/billing/status'),
    prices: () =>
      apiFetch<{
        prices: {
          tier: string;
          calendar_limit: number;
          amount: number;
          currency: string;
          interval: string;
          formatted: string;
        }[];
      }>('/billing/prices'),
    checkout: (tier: 'starter' | 'standard' | 'pro') =>
      apiFetch<{ url: string }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ tier }),
      }),
    portal: () =>
      apiFetch<{ url: string }>('/billing/portal', {
        method: 'POST',
      }),
  },

  subscriptions: {
    list: (calendarId: string) =>
      apiFetch<{ subscriptions: CalendarSubscription[] }>(
        `/calendars/${calendarId}/subscriptions`
      ),
    create: (body: {
      join_token: string;
      subscriber_calendar_id: string;
      color_override?: string;
    }) =>
      apiFetch<{ subscription: CalendarSubscription }>(
        '/subscriptions',
        { method: 'POST', body: JSON.stringify(body) }
      ),
    update: (
      id: string,
      body: Partial<{
        color_override: string | null;
      }>
    ) =>
      apiFetch<{ subscription: CalendarSubscription }>(
        `/subscriptions/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
        }
      ),
    delete: (id: string) =>
      apiFetch<{ deleted: boolean }>(
        `/subscriptions/${id}`,
        {
          method: 'DELETE',
        }
      ),
  },

  subscribers: {
    list: (calendarId: string) =>
      apiFetch<{ subscribers: CalendarSubscriber[] }>(
        `/calendars/${calendarId}/subscribers`
      ),
  },
};
